import React from 'react';
import { Link } from 'react-router-dom';
import { Camera, Share2, Tag, ExternalLink, MousePointerClick, Phone, Clock } from 'lucide-react';
import SlideDrawer from '../SlideDrawer';
import { formatPrice } from '../../utils/formatters';

const CATEGORY_CONFIG = {
  healthy: {
    title: 'En forme',
    badgeClass: 'bg-green-500/15 text-green-400 border-green-500/20',
  },
  toWatch: {
    title: 'À surveiller',
    badgeClass: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
  },
  dormant: {
    title: 'Dormant',
    badgeClass: 'bg-red-500/15 text-red-400 border-red-500/20',
  },
  topVehicles: {
    title: 'Les plus consultés (30j)',
    badgeClass: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  },
  noInterest: {
    title: 'Sans intérêt',
    badgeClass: 'bg-gray-500/15 text-gray-400 border-gray-500/20',
  },
};

const getSuggestion = (vehicle, category) => {
  const photoCount = vehicle.images?.length || 0;
  const platformCount = (vehicle.published_urls || []).filter(u => !u.unpublished).length;

  if (category === 'healthy') {
    return {
      text: `Bien diffusé, ${platformCount || 1} plateforme${platformCount > 1 ? 's' : ''} active${platformCount > 1 ? 's' : ''}`,
      color: 'text-green-400',
      bg: 'bg-green-500/[0.06]',
      border: 'border-green-500/15',
    };
  }

  if (category === 'toWatch') {
    if (photoCount === 0) {
      return {
        text: 'Ajouter des photos pour améliorer la visibilité',
        color: 'text-amber-400',
        bg: 'bg-amber-500/[0.06]',
        border: 'border-amber-500/15',
        icon: Camera,
      };
    }
    if (platformCount < 2) {
      return {
        text: 'Publier sur plus de plateformes',
        color: 'text-amber-400',
        bg: 'bg-amber-500/[0.06]',
        border: 'border-amber-500/15',
        icon: Share2,
      };
    }
    return {
      text: `${vehicle.daysInStock}j en stock — envisager une mise en avant`,
      color: 'text-amber-400',
      bg: 'bg-amber-500/[0.06]',
      border: 'border-amber-500/15',
    };
  }

  // dormant
  if (photoCount < 3) {
    return {
      text: 'Ajouter des photos — les annonces avec 5+ photos se vendent plus vite',
      color: 'text-red-400',
      bg: 'bg-red-500/[0.06]',
      border: 'border-red-500/15',
      icon: Camera,
    };
  }
  if (platformCount < 2) {
    return {
      text: 'Diffuser sur plus de plateformes',
      color: 'text-red-400',
      bg: 'bg-red-500/[0.06]',
      border: 'border-red-500/15',
      icon: Share2,
    };
  }
  return {
    text: 'Envisager une baisse de prix pour relancer l\'intérêt',
    color: 'text-red-400',
    bg: 'bg-red-500/[0.06]',
    border: 'border-red-500/15',
    icon: Tag,
  };
};

const VehicleCard = ({ vehicle, category }) => {
  const suggestion = getSuggestion(vehicle, category);
  const SugIcon = suggestion.icon || null;
  const thumb = vehicle.images?.[0];
  const title = [vehicle.make, vehicle.model].filter(Boolean).join(' ');

  return (
    <Link
      to={`/vehicles/${vehicle._id}`}
      className="block bg-dark-surface rounded-lg border border-dark-border p-3 hover:border-dark-hover transition-colors"
    >
      <div className="flex gap-3">
        {/* Thumbnail */}
        <div className="w-16 h-12 rounded-md overflow-hidden flex-shrink-0 bg-dark-hover">
          {thumb ? (
            <img src={thumb} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Camera className="h-4 w-4 text-muted" />
            </div>
          )}
        </div>
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-primary truncate">{title || 'Véhicule'}</p>
            <ExternalLink className="h-3 w-3 text-muted flex-shrink-0" />
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-xs text-muted">
            {vehicle.year && <span>{vehicle.year}</span>}
            {vehicle.daysInStock !== undefined && <span>· {vehicle.daysInStock}j en stock</span>}
            {vehicle.price && <span>· {formatPrice(vehicle.price)}</span>}
          </div>
        </div>
      </div>
      {/* Suggestion badge */}
      <div className={`mt-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs ${suggestion.bg} ${suggestion.border} border`}>
        {SugIcon && <SugIcon className={`h-3 w-3 flex-shrink-0 ${suggestion.color}`} />}
        <span className={suggestion.color}>{suggestion.text}</span>
      </div>
    </Link>
  );
};

const InterestVehicleCard = ({ vehicle, category }) => {
  const thumb = vehicle.vehicle_image;
  const isTop = category === 'topVehicles';

  return (
    <Link
      to={`/vehicles/${vehicle.vehicle_id}`}
      className="block bg-dark-surface rounded-lg border border-dark-border p-3 hover:border-dark-hover transition-colors"
    >
      <div className="flex gap-3">
        <div className="w-16 h-12 rounded-md overflow-hidden flex-shrink-0 bg-dark-hover">
          {thumb ? (
            <img src={thumb} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Camera className="h-4 w-4 text-muted" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-primary truncate">{vehicle.vehicle_name || 'Véhicule'}</p>
            <ExternalLink className="h-3 w-3 text-muted flex-shrink-0" />
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-xs text-muted">
            {vehicle.price > 0 && <span>{formatPrice(vehicle.price)}</span>}
          </div>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-3 text-xs">
        {isTop ? (
          <>
            <span className="flex items-center gap-1 text-blue-400">
              <MousePointerClick className="h-3 w-3" /> {vehicle.clicks} clic{vehicle.clicks !== 1 ? 's' : ''}
            </span>
            <span className="flex items-center gap-1 text-emerald-400">
              <Phone className="h-3 w-3" /> {vehicle.contacts} contact{vehicle.contacts !== 1 ? 's' : ''}
            </span>
          </>
        ) : (
          <span className="flex items-center gap-1 text-muted">
            <Clock className="h-3 w-3" /> {vehicle.days_online}j en ligne, 0 clics
          </span>
        )}
      </div>
    </Link>
  );
};

const INTEREST_CATEGORIES = new Set(['topVehicles', 'noInterest']);

const StockHealthDrawer = ({ open, category, vehicles, onClose }) => {
  const cfg = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.healthy;
  const isInterest = INTEREST_CATEGORIES.has(category);

  return (
    <SlideDrawer open={open} onClose={onClose} title={cfg.title}>
      {vehicles && vehicles.length > 0 ? (
        <div className="space-y-3">
          <p className="text-xs text-muted">{vehicles.length} véhicule{vehicles.length > 1 ? 's' : ''}</p>
          {vehicles.map(v =>
            isInterest ? (
              <InterestVehicleCard key={v.vehicle_id} vehicle={v} category={category} />
            ) : (
              <VehicleCard key={v._id} vehicle={v} category={category} />
            )
          )}
        </div>
      ) : (
        <p className="text-sm text-muted text-center py-8">Aucun véhicule dans cette catégorie</p>
      )}
    </SlideDrawer>
  );
};

export default StockHealthDrawer;
